import type { Quest as QuestType } from 'types/quests'
import {
    mediaBucketsEnabled,
    bucketedMediaHeight,
    RESERVED_URL_CARD_HEIGHT,
    RESERVED_DIMLESS_MEDIA_HEIGHT,
    POST_EMBED_CHROME_HEIGHT,
} from 'lib/mediaBuckets'
import { estimateTweetCardProjectionHeight } from 'lib/tweetCardHeight'
import { resolveHeightEstimationSource } from 'lib/answerHeightFingerprint'
import { resolveAnswerReservedHeight } from 'lib/answerGeometryFrame'
import { resolveAnswerGeometryProjection } from 'lib/answerGeometryProjection'
import {
    resolveDirectMessageLatestAnswer,
    resolveQuestStreamRenderedAnswers,
    resolveQuestStreamVisibleAnswerCount,
} from 'lib/questStreamRenderedAnswers'
import { isDirectMessageRoom } from 'lib/directMessages'
import {
    QUEST_STREAM_CRUMB_CHROME_PX,
    QUEST_STREAM_REASON_LABEL_CHROME_PX,
} from 'lib/staticGeometryChrome'
import { lightweightThreadInStreamEnabled } from 'lib/lightweightThreadInStream'
import { resolveQuestParentQuestId } from 'state/questAncestors'
import {
    OTHERS_ROW_ESTIMATE_PX,
    isPostLensStreamQuest,
    resolveQuestStreamOthersRow,
    streamReplyCardsEnabled,
} from 'lib/streamCards'

export {
    resolveQuestStreamPreviewAnswers,
    resolveQuestStreamRenderedAnswers,
    resolveQuestStreamVisibleAnswerCount,
} from 'lib/questStreamRenderedAnswers'

const THIN_STREAM_QUEST_ESTIMATE_PX = 50
const QUEST_STREAM_PREVIEW_ANSWER_COUNT = 3
const QUEST_STREAM_MIN_ESTIMATE_PX = 150
const QUEST_STREAM_MAX_ESTIMATE_PX = 1800
// With stream reply cards enabled, per-answer reservations can legitimately stack taller
// (see per-answer ceiling).
const QUEST_STREAM_MAX_BUCKETED_ESTIMATE_PX = 2600
// Card box around the answers container: borders 2 + shell/bottom padding. Recalibrated
// 2026-07 against rendered minimal cards (actual ~15px): the old 46 predated the explicit
// crumb/reason-label terms below and rendered as a constant blank band inside every pinned
// card. Keep chrome tight and model header add-ons explicitly instead.
const QUEST_CARD_CHROME_ESTIMATE_PX = 16
// ThreadInfo renders inside the card when preview replies are present. The old 45px reserve
// over-counted it and produced the static bottom-padding band; zeroing it clips the real 44px
// thin reply rows at the bottom of compact title cards. Keep the strip calibrated to the
// rendered compact row instead of using the larger historical value. Under the
// lightweight_thread_in_stream flag the strip never renders on stream cards and the reserve
// is skipped entirely (see shouldReserveTitleThreadInfo).
const QUEST_TITLE_COMPACT_THREAD_INFO_CHROME_PX = 36
// ThinParent breadcrumb ("Public / parent preview") is the first row inside every non-system
// card. Its 24px permission icon plus 10px/4px vertical padding renders at 38px; ThinParent
// imports the shared constant to lock the common one-line state to the same height used here.
const QUEST_TITLE_ESTIMATE_PX = 112
const QUEST_REPLY_ESTIMATE_PX = 78
const QUEST_TEXT_LINE_HEIGHT_PX = 20
const QUEST_STREAM_MEDIA_MAX_HEIGHT_PX = 525
const QUEST_STREAM_MEDIA_MIN_HEIGHT_PX = 180
const QUEST_STREAM_MEDIA_VERTICAL_CHROME_PX = 24
const QUEST_STREAM_MEDIA_HORIZONTAL_INSET_PX = 30
const QUEST_STREAM_MEDIA_GRID_GAP_PX = 6
const QUEST_PREVIEW_ESTIMATE_PX = 110
const QUEST_FILE_ROW_ESTIMATE_PX = 34
const QUEST_STREAM_EXPANDED_TEXT_LINE_LIMIT = Infinity
const QUEST_STREAM_EXPANDED_TEXT_MAX_HEIGHT_PX = 6000
const QUEST_STREAM_TITLE_URL_RESERVE_ADJUSTMENT_PX = 0
const QUEST_STREAM_TITLE_MEDIA_RESERVE_ADJUSTMENT_PX = 0
const QUEST_STREAM_TITLE_VIDEO_RESERVE_ADJUSTMENT_PX = 31
const QUEST_STREAM_TITLE_EMBED_RESERVE_ADJUSTMENT_PX = 34
const QUEST_STREAM_TITLE_FULL_CONTENT_CHROME_PX = 42
const QUEST_STREAM_TITLE_MEDIA_FRAME_CHROME_PX = 42
const QUEST_STREAM_TITLE_MEDIA_URL_FRAME_CHROME_PX = 37
const QUEST_STREAM_TITLE_AI_MEDIA_FRAME_CHROME_PX = 8
const QUEST_STREAM_TITLE_ORDINAL_URL_FRAME_CHROME_PX = 18
const QUEST_STREAM_TITLE_UNSYNCED_TWEET_FRAME_CHROME_PX = 10
const QUEST_STREAM_TITLE_URL_FRAME_CHROME_PX = 13
const QUEST_STREAM_TITLE_URL_VIDEO_FRAME_CHROME_PX = 43
const QUEST_STREAM_TITLE_TEXT_FRAME_CHROME_PX = 10
const QUEST_STREAM_TITLE_MIXED_IMAGE_GRID_FRAME_CHROME_PX = 10
// System notification wrappers render without the normal stream-title action/control chrome.
// Their embedded post body is still estimated by the generic PostEmbed path; trim only the
// absent wrapper chrome so upvalue notifications do not pin a blank card footer.
const QUEST_STREAM_SYSTEM_TITLE_EMBED_RESERVE_ADJUSTMENT_PX = 44
// Answer-level reservations already include their own post chrome. Inline quest cards with
// preview replies still need a single stack-level allowance for the list frame/separators
// around the rendered answer set; applying this per answer turns into bottom padding.
const QUEST_STREAM_ANSWER_STACK_FRAME_CHROME_PX = 21
const QUEST_STREAM_STATIC_ANSWER_STACK_FRAME_CHROME_PX = 26
// Lightweight replied cards let the first thin preview own the title/reply separator, so
// the title's former 1px bottom divider is absent from the rendered stack.
const QUEST_STREAM_LIGHTWEIGHT_TITLE_DIVIDER_TRIM_PX = 1
const QUEST_STREAM_COMPACT_REPLIED_TITLE_MAX_PX = 390
const QUEST_STREAM_COMPACT_REPLIED_TITLE_ADJUSTMENT_PX = 0
const QUEST_STREAM_REPLIED_TITLE_MEDIA_URL_TRIM_PX = 37
const QUEST_STREAM_REPLIED_TITLE_URL_TRIM_PX = 20
const QUEST_STREAM_REPLIED_TITLE_TWEET_URL_TRIM_PX = 13
const QUEST_STREAM_REPLIED_TITLE_URL_VIDEO_TRIM_PX = 25

export const QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX = 620
export const QUEST_STREAM_WIDTH_REMEASURE_DELTA_PX = 24
const QUEST_STREAM_DESKTOP_VIEWPORT_WIDTH_PX = 900
// Inline-edit composer chrome above the read-mode content: toolbar, bottom controls bar,
// padding. Insurance for the reserve while the row's clip is lifted during editing; the
// composer also renders the full uncollapsed text (handled by the collapsed-flag override).
export const ANSWER_EDITING_RESERVE_EXTRA_PX = 320
const QUEST_STREAM_MAX_EXPANDED_BUCKETED_ESTIMATE_PX =
    QUEST_STREAM_EXPANDED_TEXT_MAX_HEIGHT_PX +
    QUEST_CARD_CHROME_ESTIMATE_PX +
    QUEST_TITLE_COMPACT_THREAD_INFO_CHROME_PX +
    ANSWER_EDITING_RESERVE_EXTRA_PX

// Shared width-hysteresis policy for every virtualized row surface (stream and answer tail):
// width feeds the estimators and the static geometry signatures, so both surfaces must
// re-measure on identical thresholds or their reserves drift apart on the same resize.
// Asymmetric on purpose: wrap-induced overflow arrives in whole line-height quanta, so ANY
// shrink can add a clipped line and must re-estimate immediately, while growth only leaves
// slack and can afford hysteresis against scrollbar/zoom jitter.
export function shouldRemeasureRowWidth(currentWidth: number, nextWidth: number): boolean {
    if (!nextWidth) return false

    if (nextWidth < currentWidth) return true
    return nextWidth - currentWidth >= QUEST_STREAM_WIDTH_REMEASURE_DELTA_PX
}

function clampNumber(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value))
}

function normalizedAnswerId(answer: any): string | null {
    return answer?.id == null ? null : String(answer.id)
}

function normalizedAnswerIds(value: unknown): string[] {
    return Array.isArray(value)
        ? value.map(id => (id == null ? null : String(id))).filter((id): id is string => !!id)
        : []
}

function hasArrayItems(value: unknown): value is unknown[] {
    return Array.isArray(value) && value.length > 0
}

function titleAnswerIsSystem(answer: any): boolean {
    const systemUserId =
        typeof window !== 'undefined' ? String((window as any)?.gon?.SYSTEM_USER || '') : ''
    return !!(
        answer?.is_system ||
        answer?.user?.system ||
        (systemUserId && String(answer?.user_id || '') === systemUserId)
    )
}

function stableSystemTitleEstimateWidth(answer: any, streamWidth: number): number {
    const width =
        Number.isFinite(streamWidth) && streamWidth > 0
            ? streamWidth
            : QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX
    if (!titleAnswerIsSystem(answer) || !hasArrayItems(answer?.embeds)) return width

    const viewportWidth =
        typeof window !== 'undefined' ? Number((window as any)?.innerWidth || 0) : 0
    if (viewportWidth < QUEST_STREAM_DESKTOP_VIEWPORT_WIDTH_PX) return width

    return Math.max(width, QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX)
}

function titleAnswerBlocksThreadInfo(answer: any): boolean {
    return titleAnswerIsSystem(answer) && !answer?.leaderboard_announcement
}

function shouldReserveTitleThreadInfo(
    quest: QuestType | null | undefined,
    renderedAnswers: any[],
): boolean {
    // Lightweight stream cards render without the ThreadInfo strip (it is active-thread-only).
    if (lightweightThreadInStreamEnabled()) return false

    const titleAnswer = renderedAnswers[0]
    if (!titleAnswer) return false
    if (titleAnswerBlocksThreadInfo(titleAnswer)) return false

    return resolveQuestStreamVisibleAnswerCount(quest) > 0
}

function titleThreadInfoChrome(answer: any): number {
    const source = resolveHeightEstimationSource(answer) || answer
    if (!source) return 0
    const projection = resolveAnswerGeometryProjection(source)
    if (!projection) return 0

    // Visual title rows already reserve a tall, fixed media frame whose bottom chrome absorbs
    // the compact thread-info strip. Adding it again is the 60px+ blank band reproduced by the
    // scroll manifest on image/video/AI media titles with preview replies. Mixed visual+URL
    // rows still render URL-selection/link chrome below the media and need the compact strip.
    return projection.media.streamVisualMediaCount > 0 && !projection.url
        ? 0
        : QUEST_TITLE_COMPACT_THREAD_INFO_CHROME_PX
}

function missingDeclaredPreviewAnswerCount(
    quest: QuestType | null | undefined,
    renderedAnswers: any[],
): number {
    const declaredAnswerIds = normalizedAnswerIds((quest as any)?.sorted_answer_ids)
    if (declaredAnswerIds.length === 0) return 0
    // A direct-message card is one row, its latest message: no preview rows below it.
    if (isDirectMessageRoom(quest as any)) return 0

    const renderedTitleId = normalizedAnswerId(renderedAnswers[0])
    const renderedReplyIds = new Set(
        renderedAnswers
            .slice(1)
            .map(normalizedAnswerId)
            .filter((id): id is string => !!id),
    )
    const matchingAnswerIds = normalizedAnswerIds((quest as any)?.matching_answer_ids)
    // stream_reply_cards keeps value rows on visited resting cards, so a visit cursor no longer
    // means "nothing will render below the title" — fall through to the reservation.
    if (
        matchingAnswerIds.length === 0 &&
        (quest as any)?.last_action_created_at &&
        !streamReplyCardsEnabled()
    ) {
        return 0
    }

    const expectedPreviewAnswerIds =
        matchingAnswerIds.length > 0
            ? matchingAnswerIds
            : lightweightThreadInStreamEnabled()
            ? streamReplyCardsEnabled()
                ? // Value rows: identity is unknowable pre-hydration (amounts live on the
                  // hydrated rows), but only the COUNT feeds the reservation. Resting cards
                  // render 1–2 value rows, so two deterministic stand-ins overshoot by at
                  // most one row until hydration and the measured cache corrects it.
                  declaredAnswerIds.slice(0, 2)
                : // Lightweight cards render no "latest 3" fallback, so declared-but-unhydrated
                  // replies reserve nothing.
                  []
            : declaredAnswerIds.slice(-QUEST_STREAM_PREVIEW_ANSWER_COUNT)

    return expectedPreviewAnswerIds.reduce(
        (missingCount, answerId) =>
            missingCount + (answerId === renderedTitleId || renderedReplyIds.has(answerId) ? 0 : 1),
        0,
    )
}

export function hasQuestStreamReasonLabel(quest: QuestType | null | undefined) {
    const label = String(
        (quest as any)?.notification_reason_label ||
            (quest as any)?.feed_reason_label ||
            (quest as any)?.feed_card?.filter_match_label ||
            '',
    ).trim()
    return label.length > 0
}

// The reason label renders as a single ellipsized line, so its chrome is constant — and both
// of its placements (above the frame on desktop, inside it on full-bleed mobile) are sized to
// the same total, so the shell reserve only depends on whether a label exists.
const streamReasonLabelChrome = (quest: QuestType | null | undefined) =>
    hasQuestStreamReasonLabel(quest) ? QUEST_STREAM_REASON_LABEL_CHROME_PX : 0

function streamTitleReserveAdjustment(projection: any, answer?: any) {
    if (!projection) return 0

    const hasVisualMedia = projection.media.streamVisualMediaCount > 0
    const hasRecording = projection.media.hasRecording
    const hasGenericUrlPreview =
        !!projection.url && !hasVisualMedia && projection.url.kind === 'generic'
    const hasEmbeds = projection.embeds.length > 0
    const embedAdjustment = hasEmbeds
        ? QUEST_STREAM_TITLE_EMBED_RESERVE_ADJUSTMENT_PX +
          (titleAnswerIsSystem(answer) ? QUEST_STREAM_SYSTEM_TITLE_EMBED_RESERVE_ADJUSTMENT_PX : 0)
        : 0

    return Math.max(
        hasGenericUrlPreview ? QUEST_STREAM_TITLE_URL_RESERVE_ADJUSTMENT_PX : 0,
        hasRecording
            ? QUEST_STREAM_TITLE_VIDEO_RESERVE_ADJUSTMENT_PX
            : hasVisualMedia
            ? QUEST_STREAM_TITLE_MEDIA_RESERVE_ADJUSTMENT_PX
            : 0,
        embedAdjustment,
    )
}

function streamTitleFrameChrome(projection: any) {
    if (!projection) return 0

    if (projection.contentCharCount > String(projection.text || '').length) {
        return QUEST_STREAM_TITLE_FULL_CONTENT_CHROME_PX
    }

    if (
        projection.media.streamVisualMediaCount > 0 &&
        projection.media.streamAiVisualOutputCount > 0
    ) {
        return QUEST_STREAM_TITLE_AI_MEDIA_FRAME_CHROME_PX
    }
    if (projection.media.streamVisualMediaCount > 0) {
        const mixedGridChrome = projection.url ? 0 : mixedImageGridFrameChrome(projection)
        return (
            (projection.url
                ? QUEST_STREAM_TITLE_MEDIA_URL_FRAME_CHROME_PX
                : QUEST_STREAM_TITLE_MEDIA_FRAME_CHROME_PX) + mixedGridChrome
        )
    }
    if (String(projection.url?.address || '').includes('/ordinal/')) {
        return QUEST_STREAM_TITLE_ORDINAL_URL_FRAME_CHROME_PX
    }
    if (projection.url?.kind === 'tweet' && !projection.url.hasServiceJson) {
        return QUEST_STREAM_TITLE_UNSYNCED_TWEET_FRAME_CHROME_PX
    }
    if (projection.url?.kind === 'youtube') return QUEST_STREAM_TITLE_URL_VIDEO_FRAME_CHROME_PX
    if (projection.url) return QUEST_STREAM_TITLE_URL_FRAME_CHROME_PX
    if (!projection.url && !projection.embeds.length) return QUEST_STREAM_TITLE_TEXT_FRAME_CHROME_PX

    return 0
}

function mixedImageGridFrameChrome(projection: any): number {
    const bucketHeights = Array.isArray(projection?.media?.imageTileHeights)
        ? projection.media.imageTileHeights.slice(0, 4)
        : []
    if (bucketHeights.length <= 1) return 0

    return new Set(bucketHeights).size > 1 ? QUEST_STREAM_TITLE_MIXED_IMAGE_GRID_FRAME_CHROME_PX : 0
}

function streamRepliedTitlePayloadTrim(answer: any) {
    if (!answer) return 0
    const source = resolveHeightEstimationSource(answer) || answer
    const projection = resolveAnswerGeometryProjection(source)
    if (!projection) return 0

    const hasVisualMedia = projection.media.streamVisualMediaCount > 0
    if (hasVisualMedia && projection.url) return QUEST_STREAM_REPLIED_TITLE_MEDIA_URL_TRIM_PX
    if (hasVisualMedia) return 0
    if (projection.url?.kind === 'youtube') return QUEST_STREAM_REPLIED_TITLE_URL_VIDEO_TRIM_PX
    if (projection.url) {
        return projection.url.kind === 'tweet'
            ? QUEST_STREAM_REPLIED_TITLE_TWEET_URL_TRIM_PX
            : QUEST_STREAM_REPLIED_TITLE_URL_TRIM_PX
    }
    return 0
}

function streamCompactRepliedTitleAdjustment(
    titleAnswer: any,
    titleEstimate: number,
    hasRenderedReplies: boolean,
) {
    if (!hasRenderedReplies) return 0
    if (!mediaBucketsEnabled()) return 0
    const compactTrim =
        titleEstimate <= QUEST_STREAM_COMPACT_REPLIED_TITLE_MAX_PX
            ? QUEST_STREAM_COMPACT_REPLIED_TITLE_ADJUSTMENT_PX
            : 0
    return compactTrim + streamRepliedTitlePayloadTrim(titleAnswer)
}

function estimateMediaTileHeight(projection: any, streamWidth: number) {
    const explicitWidth = projection.media.streamFirstVisualMedia.width
    const explicitHeight = projection.media.streamFirstVisualMedia.height
    const availableWidth = Math.max(
        QUEST_STREAM_MEDIA_MIN_HEIGHT_PX,
        streamWidth - QUEST_STREAM_MEDIA_HORIZONTAL_INSET_PX,
    )

    // Fixed-height buckets make the estimate a constant matching the rendered frame, so
    // the virtualizer never sees an estimate-vs-measured mismatch (no scroll jump).
    if (mediaBucketsEnabled() && explicitWidth && explicitHeight) {
        return bucketedMediaHeight(explicitWidth, explicitHeight)
    }

    // Dimensionless video renders the reserved square frame (PayloadVideo) under the flag.
    if (mediaBucketsEnabled() && projection.media.hasRecording) {
        return RESERVED_DIMLESS_MEDIA_HEIGHT
    }

    if (explicitWidth && explicitHeight) {
        return clampNumber(
            Math.round((availableWidth * explicitHeight) / explicitWidth),
            QUEST_STREAM_MEDIA_MIN_HEIGHT_PX,
            QUEST_STREAM_MEDIA_MAX_HEIGHT_PX,
        )
    }

    return clampNumber(
        availableWidth,
        QUEST_STREAM_MEDIA_MIN_HEIGHT_PX,
        QUEST_STREAM_MEDIA_MAX_HEIGHT_PX,
    )
}

function estimateStreamMediaHeight(projection: any, streamWidth: number) {
    if (mediaBucketsEnabled()) {
        const tiles = [
            ...(projection.media.imageTileHeights || []),
            ...(projection.media.aiTileHeights || []),
            ...(projection.media.hasRecording ? [projection.media.recordingHeight] : []),
        ].slice(0, 4)
        if (tiles.length === 0) return 0

        // 2-up grid: row height is the max of its pair of per-tile buckets.
        let height = QUEST_STREAM_MEDIA_VERTICAL_CHROME_PX
        for (let i = 0; i < tiles.length; i += 2) {
            height += Math.max(...tiles.slice(i, i + 2))
            if (i > 0) height += QUEST_STREAM_MEDIA_GRID_GAP_PX
        }
        return height
    }

    const mediaCount = Math.min(4, projection.media.streamVisualMediaCount)
    if (mediaCount <= 0) return 0

    const tileHeight = estimateMediaTileHeight(projection, streamWidth)
    if (mediaCount === 1) return tileHeight + QUEST_STREAM_MEDIA_VERTICAL_CHROME_PX

    const gridRows = Math.ceil(mediaCount / 2)
    const gridTileHeight = clampNumber(
        Math.floor((tileHeight - QUEST_STREAM_MEDIA_GRID_GAP_PX) / 2),
        QUEST_STREAM_MEDIA_MIN_HEIGHT_PX,
        QUEST_STREAM_MEDIA_MAX_HEIGHT_PX,
    )

    return (
        gridRows * gridTileHeight +
        (gridRows - 1) * QUEST_STREAM_MEDIA_GRID_GAP_PX +
        QUEST_STREAM_MEDIA_VERTICAL_CHROME_PX
    )
}

function estimateAnswerTextLines(
    projection: any,
    streamWidth = QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX,
) {
    const lineCount = Number(projection?.explicitLineCount || 0)
    if (lineCount > 0) return lineCount

    const charsPerLine = clampNumber(Math.floor((streamWidth - 96) / 7.25), 34, 72)
    return Math.ceil(String(projection?.text || '').length / charsPerLine)
}

function estimateStreamAnswerHeight(
    answerInput: any,
    isTitle: boolean,
    streamWidth = QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX,
    isThin = false,
    textExpanded = false,
    tweetExpanded = false,
    postLens = false,
    editMode = false,
) {
    if (isThin) {
        return resolveAnswerReservedHeight({
            answer: answerInput,
            estimatedWidth: streamWidth,
            isThin: true,
        })
    }

    // Under the flag, the quest card renders each answer through AnswerInView. Delegate the
    // per-answer estimate to that single answer-row geometry contract so the
    // virtualizer's quest-row estimate matches the sum of what the rows actually reserve/render. This
    // function was a separate, undershooting fork (title base 112 vs 199, 5- vs 8-line clamp, coarser
    // media/url/embed chrome), which is the estimate-vs-measured gap that made quest cards correct on
    // first mount and scroll-up. The legacy (flag-off) model below is unchanged.
    if (mediaBucketsEnabled()) {
        const answerForEstimate = resolveHeightEstimationSource(answerInput)
        const answerEstimateWidth =
            isTitle && !isThin
                ? stableSystemTitleEstimateWidth(answerForEstimate, streamWidth)
                : streamWidth
        const projection = resolveAnswerGeometryProjection(answerForEstimate)
        const reservedHeight = resolveAnswerReservedHeight({
            answer: answerForEstimate,
            estimatedWidth: answerEstimateWidth,
            isTitle,
            isThin,
            postLens,
            editMode,
            maxPayloadHeight: QUEST_STREAM_MEDIA_MAX_HEIGHT_PX,
            maxReservedHeight: textExpanded ? QUEST_STREAM_EXPANDED_TEXT_MAX_HEIGHT_PX : undefined,
            textLineLimit: textExpanded ? QUEST_STREAM_EXPANDED_TEXT_LINE_LIMIT : undefined,
            textIsCollapsed: !textExpanded,
            useFullContentMetadata: textExpanded || isTitle,
            tweetExpanded,
        })
        return isTitle && !isThin
            ? Math.max(
                  QUEST_TITLE_ESTIMATE_PX,
                  reservedHeight -
                      streamTitleReserveAdjustment(projection, answerForEstimate) +
                      streamTitleFrameChrome(projection),
              )
            : reservedHeight
    }

    const fallback = isTitle ? QUEST_TITLE_ESTIMATE_PX : QUEST_REPLY_ESTIMATE_PX
    // Evicted answers remount as bare `{id, _placeholder}` stubs; substitute the retained
    // height fingerprint so the estimate stays exact instead of collapsing to the fallback.
    const answer = mediaBucketsEnabled() ? resolveHeightEstimationSource(answerInput) : answerInput
    const projection = resolveAnswerGeometryProjection(answer)
    if (!answer || !projection) return fallback

    const maxLines = isTitle ? 5 : 4
    const lineCount = clampNumber(
        estimateAnswerTextLines(projection, streamWidth) || 1,
        1,
        maxLines,
    )
    let height = fallback + (lineCount - 1) * QUEST_TEXT_LINE_HEIGHT_PX

    height += estimateStreamMediaHeight(projection, streamWidth)

    if (mediaBucketsEnabled()) {
        // An answer with visual media renders its url as a plain link, not a preview card.
        if (projection.url && projection.media.streamVisualMediaCount === 0) {
            // Tweets render via our local TwitterUrl card, whose height tracks text length +
            // media dims rather than a constant. Everything else gets the constant card.
            height +=
                projection.url.kind === 'tweet'
                    ? estimateTweetCardProjectionHeight(projection.url.tweet)
                    : RESERVED_URL_CARD_HEIGHT
        }
        if (projection.embeds.length) {
            // Embedded answers (PostEmbed) render a compact text preview: chrome plus the
            // embed's own text when serialized, or the bare stub placeholder otherwise.
            projection.embeds.forEach((embed: any) => {
                height += embed.looksLikeAnswer
                    ? POST_EMBED_CHROME_HEIGHT +
                      QUEST_REPLY_ESTIMATE_PX +
                      (clampNumber(
                          estimateAnswerTextLines(embed.projection, streamWidth) || 1,
                          1,
                          15,
                      ) -
                          1) *
                          QUEST_TEXT_LINE_HEIGHT_PX
                    : POST_EMBED_CHROME_HEIGHT + QUEST_REPLY_ESTIMATE_PX
            })
        }
    } else if (projection.url || projection.embeds.length) {
        height += QUEST_PREVIEW_ESTIMATE_PX
    }
    if (projection.fileRows.length) {
        height += projection.fileRows.length * QUEST_FILE_ROW_ESTIMATE_PX
    }
    if (projection.pollOptionCount) {
        height += Math.min(4, projection.pollOptionCount) * 28
    }

    // Buckets can stack a 500px portrait frame plus a recursively-estimated embed, so the
    // flagged ceiling must clear the tallest legitimate reservation — a clipped estimate is a
    // reintroduced estimate-vs-render mismatch.
    const ceiling = mediaBucketsEnabled() ? (isTitle ? 1500 : 1400) : isTitle ? 980 : 860
    return clampNumber(height, fallback, ceiling)
}

// Whether a thread hangs off a post in another thread (has crumbs above it).
function questHasParentThread(quest: QuestType): boolean {
    const q = quest as any
    return Boolean(
        resolveQuestParentQuestId({
            questId: q.id != null ? String(q.id) : null,
            parentQuestId: q.parent_quest_id,
            parentId: q.parent_id,
            parentAnswer: q.parent,
        }),
    )
}

export function estimateQuestStreamHeight(
    quest: QuestType | null | undefined,
    isThinQuest: boolean,
    streamWidth = QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX,
    geometryState: {
        expandedAnswerIds?: Array<string | number>
        expandedTweetAnswerIds?: Array<string | number>
        editingAnswerIds?: Array<string | number>
        // fixed_header, a channel's stream: root-thread cards carry no crumb
        // strip (ThinParent channelCard), so they reserve none.
        omitRootCrumb?: boolean
    } = {},
) {
    if (isThinQuest) return THIN_STREAM_QUEST_ESTIMATE_PX
    if (!quest) return QUEST_STREAM_MIN_ESTIMATE_PX

    const staticGeometryEnabled = mediaBucketsEnabled()
    const estimatedAnswers = resolveQuestStreamRenderedAnswers(quest)
    const missingPreviewAnswerCount = missingDeclaredPreviewAnswerCount(quest, estimatedAnswers)
    const expandedAnswerIds = new Set(
        Array.isArray(geometryState?.expandedAnswerIds)
            ? geometryState.expandedAnswerIds.map(id => String(id))
            : [],
    )
    const expandedTweetAnswerIds = new Set(
        Array.isArray(geometryState?.expandedTweetAnswerIds)
            ? geometryState.expandedTweetAnswerIds.map(id => String(id))
            : [],
    )
    const editingAnswerIds = new Set(
        Array.isArray(geometryState?.editingAnswerIds)
            ? geometryState.editingAnswerIds.map(id => String(id))
            : [],
    )
    const reservesTitleThreadInfo = shouldReserveTitleThreadInfo(quest, estimatedAnswers)
    // stream_reply_cards: the title row of a known-empty branch card renders in Answer's
    // postLens (reply) anatomy — its media clamps to the indented reply column, so the
    // estimate must apply the same width contract (lib/mediaBuckets).
    const postLensQuest = isPostLensStreamQuest(quest) || !!resolveDirectMessageLatestAnswer(quest)
    const rendersPreviewReplies = estimatedAnswers.length > 1 || missingPreviewAnswerCount > 0
    // The replied-title payload trim tracks whether reply rows actually mount below the
    // title. Off-flag that coincides with the ThreadInfo reserve; under the lightweight
    // flag the strip never reserves, so follow the rendered reply rows instead.
    const repliedTitleRendersReplies = lightweightThreadInStreamEnabled()
        ? rendersPreviewReplies
        : reservesTitleThreadInfo
    let hasExpandedAnswerGeometry = false
    let hasExpandedTweetGeometry = false
    let hasEditingGeometry = false
    const estimatedAnswerHeights = estimatedAnswers.map((answer, ix) => {
        const answerId = normalizedAnswerId(answer)
        const textExpanded = !!answerId && expandedAnswerIds.has(answerId)
        const tweetExpanded = !!answerId && expandedTweetAnswerIds.has(answerId)
        const isEditing = !!answerId && editingAnswerIds.has(answerId)
        hasExpandedAnswerGeometry ||= textExpanded
        hasExpandedTweetGeometry ||= tweetExpanded
        hasEditingGeometry ||= isEditing
        return (
            estimateStreamAnswerHeight(
                answer,
                ix === 0,
                streamWidth,
                ix > 0,
                // The composer renders the full uncollapsed text.
                textExpanded || isEditing,
                tweetExpanded,
                ix === 0 && postLensQuest,
                isEditing,
            ) + (isEditing ? ANSWER_EDITING_RESERVE_EXTRA_PX : 0)
        )
    })
    const hasResizeBackedGeometryState =
        hasExpandedAnswerGeometry || hasExpandedTweetGeometry || hasEditingGeometry
    const compactRepliedTitleAdjustment = streamCompactRepliedTitleAdjustment(
        estimatedAnswers[0],
        estimatedAnswerHeights[0] || 0,
        repliedTitleRendersReplies,
    )
    const answerStackFrameChrome = staticGeometryEnabled
        ? QUEST_STREAM_STATIC_ANSWER_STACK_FRAME_CHROME_PX
        : QUEST_STREAM_ANSWER_STACK_FRAME_CHROME_PX
    const lightweightTitleDividerTrim =
        lightweightThreadInStreamEnabled() &&
        rendersPreviewReplies &&
        !titleAnswerBlocksThreadInfo(estimatedAnswers[0])
            ? QUEST_STREAM_LIGHTWEIGHT_TITLE_DIVIDER_TRIM_PX
            : 0
    // Mirrors Quest.jsx's card/shell chrome: ThinParent renders inside every non-system card,
    // and the reason label contributes its fixed chrome above the card (desktop and phones).
    // A direct-message card's header (DirectMessageCardHeader) is sized to the crumb reserve.
    const rendersCrumb =
        !!resolveDirectMessageLatestAnswer(quest) ||
        (!!estimatedAnswers[0] &&
            !titleAnswerIsSystem(estimatedAnswers[0]) &&
            !(geometryState?.omitRootCrumb && !questHasParentThread(quest)))
    const reasonLabelChrome = streamReasonLabelChrome(quest)
    // stream_reply_cards: the others row is card chrome below the last reply (it resolves
    // to null on post-lens cards — no visible answers). Post-lens cards render the title
    // answer through the standard stack in reply anatomy, so its estimate path IS the
    // title-answer path; the measured cache absorbs the anatomy residual.
    const othersRowChrome = resolveQuestStreamOthersRow(quest, estimatedAnswers)
        ? OTHERS_ROW_ESTIMATE_PX
        : 0
    const estimatedHeight =
        othersRowChrome +
        QUEST_CARD_CHROME_ESTIMATE_PX +
        (rendersCrumb ? QUEST_STREAM_CRUMB_CHROME_PX : 0) +
        reasonLabelChrome +
        (reservesTitleThreadInfo ? titleThreadInfoChrome(estimatedAnswers[0]) : 0) +
        (rendersPreviewReplies ? answerStackFrameChrome : 0) +
        missingPreviewAnswerCount * estimateStreamAnswerHeight(null, false, streamWidth, true) +
        estimatedAnswerHeights.reduce((total, height) => total + height, 0) -
        compactRepliedTitleAdjustment -
        lightweightTitleDividerTrim

    return clampNumber(
        estimatedHeight,
        QUEST_STREAM_MIN_ESTIMATE_PX,
        staticGeometryEnabled
            ? hasResizeBackedGeometryState
                ? QUEST_STREAM_MAX_EXPANDED_BUCKETED_ESTIMATE_PX
                : QUEST_STREAM_MAX_BUCKETED_ESTIMATE_PX
            : QUEST_STREAM_MAX_ESTIMATE_PX,
    )
}
